/**
 * Upcoming-episode store — admin schedules the next episode release.
 *
 * PUBLIC source of truth: `/upcoming-episode.json`.
 * This file must live in `public/upcoming-episode.json` in the repository so
 * Vite/Cloudflare includes it in the deployed site root.
 *
 * localStorage is only a fast private cache for the admin browser. Public
 * visitors always fetch the published JSON file, so another browser/device can
 * see the widget after the site rebuild finishes.
 */
import { useEffect, useState } from "react";

const KEY = "rawan-upcoming-episode";
const PUBLIC_URLS = ["/upcoming-episode.json", "/content/upcoming-episode.json"];

export type UpcomingEpisode = {
  seriesSlug: string;
  seriesTitle: string;
  seriesTitleEn?: string;
  seasonNumber: number;
  episodeNumber: number;
  releaseAtIso: string;
  message?: string;
  releasedMessage?: string;
  episodeUrl?: string;
  updatedAt: string;
};

function isValidUpcoming(value: unknown): value is UpcomingEpisode {
  if (!value || typeof value !== "object") return false;
  const u = value as Partial<UpcomingEpisode>;
  return Boolean(
    typeof u.seriesSlug === "string" && u.seriesSlug.trim() &&
    typeof u.releaseAtIso === "string" && u.releaseAtIso.trim() &&
    Number.isFinite(new Date(u.releaseAtIso).getTime())
  );
}

export function getUpcoming(): UpcomingEpisode | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return isValidUpcoming(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

export function setUpcoming(u: UpcomingEpisode | null): void {
  if (typeof window === "undefined") return;
  if (!u) localStorage.removeItem(KEY);
  else localStorage.setItem(KEY, JSON.stringify({ ...u, updatedAt: new Date().toISOString() }));
  window.dispatchEvent(new CustomEvent("rawan:upcoming-changed"));
}

function pickFreshest(a: UpcomingEpisode | null, b: UpcomingEpisode | null): UpcomingEpisode | null {
  if (!a) return b;
  if (!b) return a;
  const ta = Date.parse(a.updatedAt) || 0;
  const tb = Date.parse(b.updatedAt) || 0;
  return tb >= ta ? b : a;
}

async function fetchPublicUpcoming(): Promise<UpcomingEpisode | null> {
  for (const publicUrl of PUBLIC_URLS) {
    try {
      const res = await fetch(`${publicUrl}?v=${Date.now()}`, { cache: "no-store" });
      if (!res.ok) continue;
      const json = await res.json();
      if (isValidUpcoming(json)) return json;
    } catch {
      // Try the next possible public path.
    }
  }
  return null;
}

export function useUpcoming(): UpcomingEpisode | null {
  const [u, setU] = useState<UpcomingEpisode | null>(() => getUpcoming());

  useEffect(() => {
    const sync = () => setU(getUpcoming());
    window.addEventListener("rawan:upcoming-changed", sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener("rawan:upcoming-changed", sync);
      window.removeEventListener("storage", sync);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    fetchPublicUpcoming().then((remote) => {
      if (cancelled) return;
      setU((local) => {
        const winner = pickFreshest(local, remote);
        // Only refresh the localStorage cache when the REMOTE value is the
        // freshest. Otherwise a stale published JSON (older `updatedAt`)
        // would overwrite a newer draft that the admin just scheduled —
        // the widget would then vanish (or show a past release as the
        // "fire" state) after a Cloudflare rebuild + page refresh.
        try {
          if (winner && winner === remote) {
            localStorage.setItem(KEY, JSON.stringify(winner));
          } else if (!winner) {
            localStorage.removeItem(KEY);
          }
        } catch { /* ignore cache errors */ }
        return winner;
      });
    });
    return () => { cancelled = true; };
  }, []);

  return u;
}

export function useCountdown(): { upcoming: UpcomingEpisode | null; diffMs: number; expired: boolean; now: Date } {
  const upcoming = useUpcoming();
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    if (!upcoming) return;
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, [upcoming]);
  const diffMs = upcoming ? new Date(upcoming.releaseAtIso).getTime() - now.getTime() : 0;
  return { upcoming, diffMs, expired: diffMs <= 0, now };
}

export function breakdown(diffMs: number) {
  const ms = Math.max(0, diffMs);
  const days = Math.floor(ms / 86_400_000);
  const hours = Math.floor((ms % 86_400_000) / 3_600_000);
  const minutes = Math.floor((ms % 3_600_000) / 60_000);
  const seconds = Math.floor((ms % 60_000) / 1000);
  return { days, hours, minutes, seconds };
}
